import type { ProjectVisibilityOwnBrand, ProjectVisibilityTopBrand } from '../types';

/** Matches backend brand table ordering: visibility desc, then avg position asc. */
export function sortVisibilityBrands(
  brands: ProjectVisibilityTopBrand[],
): ProjectVisibilityTopBrand[] {
  return [...brands].sort((a, b) => {
    if (b.visibilityScore !== a.visibilityScore) {
      return b.visibilityScore - a.visibilityScore;
    }
    const aPos = a.avgPosition ?? Number.MAX_SAFE_INTEGER;
    const bPos = b.avgPosition ?? Number.MAX_SAFE_INTEGER;
    if (aPos !== bPos) return aPos - bPos;
    return b.aiMentions - a.aiMentions;
  });
}

export function isOwnBrandRow(brand: ProjectVisibilityTopBrand): boolean {
  return brand.relationship === 'you';
}

export function findOwnBrandRow(
  brands: ProjectVisibilityTopBrand[],
): ProjectVisibilityTopBrand | null {
  return brands.find((brand) => brand.relationship === 'you') ?? null;
}

/** KPI prompt coverage — prefer aggregated topBrands row over summary.ownBrand. */
export function resolvePromptCoverageKpi(input: {
  ownBrand: ProjectVisibilityOwnBrand | null;
  ownBrandRow: ProjectVisibilityTopBrand | null;
  domainBrand: ProjectVisibilityTopBrand | null;
  useOwnBrandScope: boolean;
}): { coverage: number | null; brandName: string | null } {
  const row = input.useOwnBrandScope
    ? input.ownBrandRow
    : input.domainBrand ?? input.ownBrandRow;
  const coverage = row?.promptCoverage ?? input.ownBrand?.promptCoverage ?? null;
  const brandName = row?.canonicalName ?? input.ownBrand?.canonicalName ?? null;
  return { coverage, brandName };
}

export function formatPromptCoverageValue(
  coverage: number | null,
  executedPrompts: number,
  totalPrompts: number,
): string {
  if (coverage == null) return '—';
  return `${coverage} / ${executedPrompts || totalPrompts}`;
}

export function formatProjectVisibilityDelta(delta: number | null): string {
  if (delta == null) return 'نسبت به دوره قبل';
  if (delta > 0) return `↑ ${delta} واحد نسبت به دوره قبل`;
  if (delta < 0) return `↓ ${Math.abs(delta)} واحد نسبت به دوره قبل`;
  return 'بدون تغییر نسبت به دوره قبل';
}

/** Positive gap = you lead the competitor. */
export function computeVisibilityGapVsYou(
  ownScore: number | null | undefined,
  competitorScore: number,
): number | null {
  if (ownScore == null) return null;
  return ownScore - competitorScore;
}

export function formatCompetitorGapLabel(
  gap: number | null,
  competitorName: string,
): { text: string; tone: 'ahead' | 'behind' | 'tied' | 'neutral' } {
  if (gap == null) return { text: '—', tone: 'neutral' };
  if (gap > 0) return { text: `شما +${gap} جلوتر`, tone: 'ahead' };
  if (gap < 0) return { text: `${competitorName} +${Math.abs(gap)} جلوتر`, tone: 'behind' };
  return { text: 'مساوی', tone: 'tied' };
}

/** KPI visibility score — own brand row when unfiltered, domain brand when filtered. */
export function resolveVisibilityScoreKpi(input: {
  ownBrand: ProjectVisibilityOwnBrand | null;
  ownBrandRow: ProjectVisibilityTopBrand | null;
  domainBrand: ProjectVisibilityTopBrand | null;
  domainVisibilityScore: number | null;
  useOwnBrandScope: boolean;
}): { score: number | null; delta: number | null } {
  if (!input.useOwnBrandScope) {
    return { score: input.domainVisibilityScore, delta: null };
  }

  const row = input.ownBrandRow;
  const score = row?.visibilityScore ?? input.ownBrand?.visibilityScore ?? null;
  const delta = row?.changePercentage ?? null;
  return { score, delta };
}
